(async () => {
  if (!customElements.get("product-form")) {
    const { Fancybox } = await import("@fancyapps/ui");
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        // Cache all DOM elements in constructor to avoid repeated queries
        this.cartNotification = document.querySelector("cart-notification");
        this.cartDrawer = document.querySelector("cart-drawer");
        this.quickShop = this.closest("quick-shop");
        this.stickyAddToCart = document.querySelector("[data-sticky-add-to-cart]");
        this.formToggleButton = document.querySelector("[data-form-toggle]");
        this.mainCartItems = document.querySelector("cart-items");
        this.cartIconBubble = document.querySelector("cart-icon-bubble");
        this.mixedCart = document.querySelector(".mixed-cart");
        this.progressBar = document.querySelector("progress-bar");
        this.freeGift = document.querySelector("free-gift");

        // Cache form-related elements
        this.form = this.querySelector("form");
        this.productForm = document.querySelector("[data-product-variants]");
        this.addToCartBtn = document.querySelector("[data-add-to-cart-btn]");
        this.mobileVariants = document.querySelector(".product__form-wrapper");
        this.backdrop = document.querySelector("[data-form-backdrop]");

        this.form.addEventListener("submit", (event) => {
          event.preventDefault();

          // Check if the component is inside the quick shop component
          if (this.quickShop) {
            this.quickShop.querySelector("[data-quick-shop-option]:checked")
              ? this.onSubmitHandler()
              : "";
          } else {
            this.onSubmitHandler();
          }
          this.updateProgressBarVisibility();
        });
      }

      updateProgressBarVisibility() {
        // Update visibility free gift progress bar using cached elements
        let giftCards = document.querySelectorAll('[data-item-type="giftcard"]');

        // if there are no gift cards in the cart
        const hasGiftCards = giftCards.length > 0;
        if (this.progressBar && this.freeGift) {
          // Batch DOM updates
          const shouldHide = hasGiftCards;
          this.progressBar.classList.toggle("hidden", shouldHide);
          this.freeGift.classList.toggle("hidden", shouldHide);
        }
      }

      updateStickyForm() {
        // Batch DOM updates using cached elements
        if (this.formToggleButton) {
          this.formToggleButton.classList.remove("visibility-hidden");
        }
        if (this.productForm) {
          this.productForm.classList.add("visibility-hidden");
        }
        if (this.backdrop) {
          this.backdrop.classList.add("visibility-hidden");
        }
      }

      updateForm() {
        // Use cached elements and optimize DOM operations
        if (!this.productForm || !this.addToCartBtn) return;

        // Find and uncheck size options more efficiently
        const checkedSizeOption = this.productForm.querySelector(".options__input--size:checked");
        if (checkedSizeOption) {
          checkedSizeOption.checked = false;
        }

        // Update add to cart button
        this.addToCartBtn.setAttribute("disabled", true);

        // Determine button text based on classes (cached check)
        let buttonText;
        if (this.addToCartBtn.classList.contains("private-sale")) {
          buttonText = window.variantStrings.selectSize;
        } else if (this.addToCartBtn.classList.contains("is-logged-in")) {
          buttonText = window.variantStrings.selectSize;
        } else if (this.addToCartBtn.classList.contains("giftcard")) {
          buttonText = window.variantStrings.selectGiftcard;
        } else if (this.addToCartBtn.classList.contains("upsell")) {
          buttonText = window.variantStrings.upsellAddToCart;
          this.addToCartBtn.removeAttribute("disabled");
        } else {
          buttonText = window.variantStrings.selectSize;
        }
        this.addToCartBtn.textContent = buttonText;
      }

      updateMobileForm() {
        // Use cached element and optimize
        if (!this.mobileVariants) return;

        const checkedMobileSizeOption = this.mobileVariants.querySelector(".options__input--size:checked");
        if (checkedMobileSizeOption) {
          checkedMobileSizeOption.checked = false;
        }
      }

      getSectionInnerHTML(html, selector) {
        return new DOMParser()
          .parseFromString(html, "text/html")
          .querySelector(selector).innerHTML;
      }

      updateCartIconBubble(parsedState) {
        if (!this.cartIconBubble) return;

        const cartIconBubbleSection = {
          id: "cart-icon-bubble",
          section: "cart-icon-bubble",
          selector: "[data-cart-icon-bubble]",
        };

        const selector = document.querySelector(cartIconBubbleSection.selector);
        if (selector && parsedState.sections?.[cartIconBubbleSection.section]) {
          selector.innerHTML = this.getSectionInnerHTML(
            parsedState.sections[cartIconBubbleSection.section],
            cartIconBubbleSection.selector
          );
        }
      }

      // Extract free product processing into separate method for better organization
      async processFreeProduct(formData, sectionsToFetch, mainProductVariantId, mainProductQuantity) {
        const freeProductValue = formData.get("free_product_id");
        const freeProductVariantId = formData.get("free_product_variant_id");

        // Early return if no valid free product
        if ((!freeProductValue && !freeProductVariantId) ||
            freeProductValue === "null" || freeProductValue === "") {
          return null;
        }

        let variantId = freeProductVariantId ? parseInt(freeProductVariantId, 10) : null;

        // Try to get variant ID from data attributes if not already found
        if (!variantId) {
          const possibleElements = [
            this.form.querySelector("[name='free_product_id']"),
            this.form,
            this.form.querySelector("[data-free-product-variant]"),
            document.querySelector("[data-free-product-variant]"),
          ];

          for (const element of possibleElements) {
            if (element?.dataset?.freeProductVariant) {
              variantId = parseInt(element.dataset.freeProductVariant, 10);
              break;
            }
          }
        }

        // Try to parse JSON if still no variant ID
        if (!variantId && freeProductValue) {
          try {
            const freeProductData = typeof freeProductValue === "object"
              ? freeProductValue
              : JSON.parse(freeProductValue);

            if (freeProductData?.variants?.length > 0) {
              const availableVariant = freeProductData.variants.find(v => v.available);
              variantId = (availableVariant || freeProductData.variants[0]).id;
            } else if (freeProductData?.id) {
              variantId = freeProductData.id;
            }
          } catch (jsonError) {
            // Continue to next method
          }
        }

        // Extract product ID from GID format and fetch product data
        if (!variantId && typeof freeProductValue === "string") {
          const match = freeProductValue.match(/gid:\/\/shopify\/Product\/(\d+)/);
          if (match?.[1]) {
            try {
              const productResponse = await fetch(`${window.location.origin}/products/${match[1]}.js`);
              if (productResponse.ok) {
                const productData = await productResponse.json();
                if (productData.variants?.length > 0) {
                  const availableVariant = productData.variants.find(v => v.available);
                  variantId = availableVariant ? availableVariant.id : productData.variants[0].id;
                }
              }
            } catch (error) {
              console.error("Error fetching product data:", error);
            }
          }
        }

        if (!variantId) return null;

        try {
          // Get current cart and check for existing free products in parallel
          const [cartResponse] = await Promise.all([
            fetch(`${routes.cart_url}.js`)
          ]);

          const cartData = await cartResponse.json();
          const existingFreeProducts = cartData.items.filter(
            item => item.variant_id === parseInt(variantId) &&
                   item.properties?._free_product === "true"
          );

          if (existingFreeProducts.length > 0) {
            // Update existing free product quantity
            const existingFreeProduct = existingFreeProducts[0];
            const lineItemPosition = cartData.items.indexOf(existingFreeProduct) + 1;
            const newQuantity = existingFreeProduct.quantity + mainProductQuantity;

            const updateConfig = fetchConfig("json");
            const cartDrawerSections = this.cartDrawer
              ? this.cartDrawer.getSectionsToRender().map(section => section.section)
              : [];

            const allSections = [...new Set([...sectionsToFetch, ...cartDrawerSections, "cart-drawer"])];

            updateConfig.body = JSON.stringify({
              line: lineItemPosition,
              quantity: newQuantity,
              properties: {
                _free_product: "true",
                _main_product_variant: mainProductVariantId,
                _parent_id: mainProductVariantId,
              },
              sections: allSections,
              sections_url: window.location.pathname,
            });

            const updateResponse = await fetch(`${routes.cart_change_url}`, updateConfig);
            return await updateResponse.json();
          } else {
            // Add new free product
            const freeProductFormData = new FormData();
            freeProductFormData.append("id", variantId);
            freeProductFormData.append("quantity", mainProductQuantity);
            freeProductFormData.append("sections", sectionsToFetch);
            freeProductFormData.append("sections_url", window.location.pathname);
            freeProductFormData.append("properties[_free_product]", "true");
            freeProductFormData.append("properties[_main_product_variant]", mainProductVariantId);
            freeProductFormData.append("properties[_parent_id]", mainProductVariantId);

            const freeProductConfig = fetchConfig("javascript");
            freeProductConfig.headers["X-Requested-With"] = "XMLHttpRequest";
            delete freeProductConfig.headers["Content-Type"];
            freeProductConfig.body = freeProductFormData;

            const freeProductResponse = await fetch(routes.cart_add_url, freeProductConfig);
            const result = await freeProductResponse.json();

            if (result.status === "bad_request") {
              console.error("Error adding free product to cart:", result);
              return null;
            }
            return result;
          }
        } catch (error) {
          console.error("Error processing free product:", error);
          return null;
        }
      }

      onSubmitHandler() {
        const submitButton = this.querySelector('[type="submit"]');
        const timeout = submitButton.dataset.closeTimeout;

        // Batch DOM updates for loading state
        submitButton.setAttribute("disabled", true);
        submitButton.classList.add("loading");
        if (this.cartIconBubble) {
          this.cartIconBubble.classList.add("is-loading");
        }

        const config = fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        delete config.headers["Content-Type"];

        // Get sections to fetch more efficiently
        let sectionsToFetch = [];

        if (this.cartDrawer && !this.cartNotification) {
          sectionsToFetch = this.cartDrawer.getSectionsToRender().map(section => section.id);
        }
        else if (this.cartNotification && !this.cartDrawer) {
          sectionsToFetch = this.cartNotification.getSectionsToRender().map(section => section.id);
        } else {
          sectionsToFetch = [
            ...this.cartNotification.getSectionsToRender().map(section => section.id),
            ...this.cartDrawer.getSectionsToRender().map(section => section.id)];
        }

        const formData = new FormData(this.form);
        formData.append("sections", sectionsToFetch);
        formData.append("sections_url", window.location.pathname);
        config.body = formData;

        // Check mixed cart logic using cached element
        if (this.mixedCart) {
          const cartHasGiftcard = !!document.querySelector('[data-item-type="giftcard"]');
          const newProduct = this.form.querySelector('input[name="product_type"]').value === "product";
          const cartHasProducts = !!document.querySelector('[data-item-type="product"]');
          const newGiftCard = this.form.querySelector('input[name="product_type"]').value === "giftcard";

          if ((cartHasGiftcard && newProduct) || (cartHasProducts && newGiftCard)) {
            this.mixedCart.renderMixedCartPopup(cartHasGiftcard && newProduct ? "product" : "giftcard");

            // Batch cleanup operations
            this.mainCartItems.disableLoading();
            this.updateStickyForm();
            submitButton.classList.remove("loading");
            submitButton.removeAttribute("disabled");
            return;
          }
        }

        return new Promise((resolve, reject) => {
          const handleAsyncSubmit = async () => {
            try {
              // Main product add to cart
              const response = await fetch(`${routes.cart_add_url}`, config);
              let parsedState = await response.json();
              const originalParsedState = { ...parsedState };

              // Process free product if exists
              const mainProductVariantId = formData.get("id");
              const mainProductQuantity = parseInt(formData.get("quantity"), 10) || 1;

              const freeProductResult = await this.processFreeProduct(
                formData,
                sectionsToFetch,
                mainProductVariantId,
                mainProductQuantity
              );

              if (freeProductResult) {
                parsedState = freeProductResult;
              }

              // Parallel fetch operations for better performance
              const [cartResponse, cartSectionsResponse] = await Promise.all([
                fetch(`${routes.cart_url}`, config),
                (() => {
                  if (this.mainCartItems) {
                    const sections = [...new Set(this.mainCartItems.getSectionsToRender().map(section => section.section))];
                    return fetch(`${window.location.pathname}?sections=${sections.join(",")}`);
                  }
                  return Promise.resolve({ json: () => ({}) });
                })()
              ]);

              const [parsedStateCart, cartSections] = await Promise.all([
                cartResponse.json(),
                cartSectionsResponse.json ? cartSectionsResponse.json() : Promise.resolve({})
              ]);

              // Update cart drawer if available
              if (this.cartDrawer) {
                try {
                  if (!parsedState.sections?.["cart-drawer"]) {
                    const drawerSections = this.cartDrawer.getSectionsToRender().map(section => section.section);
                    const cartDrawerResponse = await fetch(`${window.location.pathname}?sections=${drawerSections.join(",")}`);
                    const cartDrawerSections = await cartDrawerResponse.json();

                    if (!parsedState.sections) parsedState.sections = {};
                    Object.assign(parsedState.sections, cartDrawerSections);
                  }
                  this.cartDrawer.renderContents(parsedState);
                } catch (renderError) {
                  console.error("Error rendering cart drawer:", renderError);
                  try {
                    this.cartDrawer.renderContents(parsedStateCart);
                  } catch (fallbackError) {
                    console.error("Fallback rendering also failed:", fallbackError);
                  }
                }
              }

              // Add cart sections to parsedState
              if (cartSections && typeof cartSections === 'object') {
                if (!parsedState.sections) parsedState.sections = {};
                Object.assign(parsedState.sections, cartSections);
              }

              // Update cart page
              if (this.mainCartItems) {
                this.mainCartItems.renderContents(parsedState);
                this.mainCartItems.updateContents(parsedState);
              }

              // Update cart icon bubble with slight delay for smoother UX
              setTimeout(() => {
                this.updateCartIconBubble(parsedState);
              }, 100);

              // Determine add to cart behavior
              let addToCartBehavior = submitButton.dataset.addToCartBehavior;
              if (window.screen.width < 768 || !this.cartDrawer) {
                addToCartBehavior = "show_cart_notification";
              }

              // Handle cart behavior
              switch (addToCartBehavior) {
                case "open_cart_drawer":
                  this.cartDrawer.open();
                  if (timeout) {
                    setTimeout(() => this.cartDrawer.close(), timeout);
                  }
                  break;
                case "show_cart_notification":
                default:
                  if (this.cartNotification) {
                    this.cartNotification.setActiveElement(document.activeElement);
                    this.cartNotification.renderContents(originalParsedState || parsedState);
                  }
              }

              // Batch updates for free gift and progress bar
              if (this.freeGift) this.freeGift.update(parsedStateCart);
              if (this.progressBar) this.progressBar.update(parsedStateCart);

              // Close quickshop if present
              if (this.quickShop) {
                this.quickShop.onPopupToggle();
              }

              // Batch cleanup operations
              submitButton.classList.remove("loading");
              submitButton.removeAttribute("disabled");

              setTimeout(() => {
                if (this.cartIconBubble) {
                  this.cartIconBubble.classList.remove("is-loading");
                }
              }, 250);

              if (this.stickyAddToCart) {
                this.updateStickyForm();
                this.updateForm();
                this.updateMobileForm();
              }

              resolve();
            } catch (error) {
              console.error("Error in onSubmitHandler:", error);
              // Cleanup on error
              submitButton.classList.remove("loading");
              submitButton.removeAttribute("disabled");
              if (this.cartIconBubble) {
                this.cartIconBubble.classList.remove("is-loading");
              }
              reject(error);
            }
          };

          handleAsyncSubmit();
        });
      }
    }

    window.ProductForm = ProductForm;
    customElements.define("product-form", ProductForm);
  }
})();
